<template>
  <div class="skills">
    <h4>Skills</h4>
    <SkillRow
      v-for="skill in allSkills"
      :key="skill.name"
      :name="skill.name"
      :dice="skill.expr"
      :level="skill.level"
      :label="skill.label"
    />
  </div>
</template>

<script setup lang="ts">
import { titleCase } from '../util'
import { type CharacterSheetData, skillStats } from '../statblock/statblock'
import SkillRow from './SkillRow.vue'
import { joinDice } from '~/assets/dice/diceEval'

const props = defineProps<{
  stats: CharacterSheetData
}>()

const diceExpr = (skill: string) => {
  const stat = skillStats[skill]
  skill = titleCase(skill)
  const proficiency = (props.stats.skills || []).includes(skill)
  const base = props.stats.reliableTalent && proficiency ? 'd20t10' : 'd20'
  const prof = proficiency ? 'prof' : ''
  const exp = (props.stats.expertise || []).includes(skill) ? 'prof' : ''
  const jack = !prof && !exp && props.stats.jackOfAllTrades ? 'prof/2' : ''
  const bonus = props.stats.skillBonus ?? ''
  return joinDice(base, stat, prof, exp, jack, bonus)
}

const level = (skill: string) => {
  skill = titleCase(skill)
  if ((props.stats.expertise || []).includes(skill)) return 'expertise'
  if ((props.stats.skills || []).includes(skill)) return 'proficiency'
  if (props.stats.jackOfAllTrades) return 'halfproficiency'
  return ''
}

const allSkills = computed(() => {
  const customSkills = Object.fromEntries(
    Object.entries(props.stats.customSkills || {}).map(([name, expr]) => [
      name,
      {
        name,
        expr,
        label: titleCase(name),
        level: 'custom',
      },
    ]),
  )
  const baseSkills = Object.fromEntries(
    Object.keys(skillStats).map((skill) => [
      skill,
      {
        name: skill,
        expr: diceExpr(skill),
        label: titleCase(skill),
        level: level(skill),
      },
    ]),
  )

  return Object.values({ ...baseSkills, ...customSkills })
})
</script>

<style scoped lang="postcss">
.skills {
  grid-area: skills;
  display: flex;
  flex-direction: column;

  h4 {
    margin-top: 0;
    text-align: center;
    border-bottom: 2px solid var(--primary);
  }
}
</style>
